/**
 * ChartVisualizationParamCard body.
 *
 * Spec: v2-design/CARD_SHELL_v0_spec.md §5 (per-control inline edit).
 *
 * Layout (DA body minus from/to + dates + directionality):
 *   Output:     <picker over candidates>   (segments indented beneath)
 *   Timescale:  <Monthly / Quarterly / Yearly toggle>
 *   Chart name: <text input>
 *
 * Edit affordances:
 *   - Output: same Select-over-candidates pattern as DA, dispatches
 *     edit_field({ output: { selected, candidates } }) immediately.
 *     Drives a segments_only re-fire backend-side.
 *   - Timescale: toggle commit dispatches edit_field({ timescale }).
 *     CV does not snap dates because CV has no date fields.
 *   - Chart name: MUI TextField. Commits on blur (matches the DA
 *     DatePicker onAccept pattern — avoid per-keystroke dispatches).
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { OutputAndSegmentsBlock } from '../shared/OutputAndSegmentsBlock';

import {
  isTimescaleSelectable,
  parseSelectableTimescales,
  TIMESCALE_OPTIONS,
  TimescaleLiteral,
  UNAVAILABLE_TIMESCALE_TOOLTIP,
} from './timescaleOptions';

interface DimensionInstance {
  name: string;
  id: number;
}

interface DimensionSegment {
  dimension_depth: number;
  dimension_name: string;
  instances: DimensionInstance[];
}

interface SelectedOutput {
  name: string;
  id: number;
  selection_rationale?: string | null;
}

interface OutputCandidate {
  name: string;
  id: number;
  similarity?: number | null;
}

interface OutputSelection {
  selected: SelectedOutput;
  candidates: OutputCandidate[];
}

interface ChartVisualizationParamBodyProps {
  content: Record<string, unknown>;
  onEditField: (field_updates: Record<string, unknown>) => void;
  editsDisabled: boolean;
}

export const ChartVisualizationParamBody: React.FC<
  ChartVisualizationParamBodyProps
> = ({ content, onEditField, editsDisabled }) => {
  const output = content.output as OutputSelection;
  const segments = (content.segments ?? []) as DimensionSegment[];
  const segmentRationale =
    typeof content.segment_rationale === 'string' && content.segment_rationale
      ? content.segment_rationale
      : undefined;

  const timescale = content.timescale ? String(content.timescale) : null;
  const timescaleNote =
    typeof content.timescale_note === 'string' && content.timescale_note
      ? content.timescale_note
      : undefined;
  const selectableTimescales = parseSelectableTimescales(
    content.selectable_timescales
  );

  const chartName =
    typeof content.chart_name === 'string' ? content.chart_name : '';

  return (
    <Stack spacing={1.5}>
      <OutputAndSegmentsBlock
        output={output}
        segments={segments}
        segmentRationale={segmentRationale}
        outputControl={
          <OutputPicker
            output={output}
            onEditField={onEditField}
            disabled={editsDisabled}
          />
        }
      />

      <TimescaleControl
        timescale={timescale}
        timescaleNote={timescaleNote}
        selectableTimescales={selectableTimescales}
        onEditField={onEditField}
        disabled={editsDisabled}
      />

      <ChartNameControl
        chartName={chartName}
        onEditField={onEditField}
        disabled={editsDisabled}
      />
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Output picker — same pattern as DriverAnalysisParamBody.
// ---------------------------------------------------------------------------

interface OutputPickerProps {
  output: OutputSelection;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

const OutputPicker: React.FC<OutputPickerProps> = ({
  output,
  onEditField,
  disabled,
}) => {
  const options = useMemo<OutputCandidate[]>(() => {
    const selectedOption: OutputCandidate = {
      name: output.selected.name,
      id: output.selected.id,
    };
    return [selectedOption, ...output.candidates];
  }, [output.selected, output.candidates]);

  const handleChange = useCallback(
    (e: SelectChangeEvent<number>) => {
      const nextId = Number(e.target.value);
      if (!Number.isFinite(nextId) || nextId === output.selected.id) return;
      const pick = options.find((o) => o.id === nextId);
      if (!pick) return;
      const nextCandidates: OutputCandidate[] = output.candidates
        .filter((c) => c.id !== pick.id)
        .concat(
          pick.id === output.selected.id
            ? []
            : [{ name: output.selected.name, id: output.selected.id }]
        )
        .sort((a, b) => a.name.localeCompare(b.name));
      onEditField({
        output: {
          selected: { id: pick.id, name: pick.name },
          candidates: nextCandidates,
        },
      });
    },
    [onEditField, options, output.candidates, output.selected]
  );

  return (
    <Select
      value={output.selected.id}
      onChange={handleChange}
      size="small"
      disabled={disabled}
      variant="outlined"
      sx={{
        minWidth: 240,
        maxWidth: 360,
        fontSize: 13,
        '& .MuiSelect-select': {
          py: 0.5,
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: { maxHeight: 320 },
        },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: 13 }}>
          {opt.name}
        </MenuItem>
      ))}
    </Select>
  );
};

// ---------------------------------------------------------------------------
// Timescale control — toggle group only (no date pickers; CV has no dates).
// ---------------------------------------------------------------------------

interface TimescaleControlProps {
  timescale: string | null;
  timescaleNote?: string;
  selectableTimescales: TimescaleLiteral[] | null;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

const TimescaleControl: React.FC<TimescaleControlProps> = ({
  timescale,
  timescaleNote,
  selectableTimescales,
  onEditField,
  disabled,
}) => {
  const handleChange = useCallback(
    (_e: React.MouseEvent<HTMLElement>, next: TimescaleLiteral | null) => {
      if (!next || next === timescale) return;
      onEditField({ timescale: next });
    },
    [onEditField, timescale]
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: 'text.secondary',
          width: 120,
          flexShrink: 0,
          lineHeight: 1.5,
          pt: 0.75,
        }}
      >
        Timescale
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={1}>
          <ToggleButtonGroup
            value={timescale ?? ''}
            exclusive
            onChange={handleChange}
            size="small"
            disabled={disabled}
            aria-label="Timescale"
          >
            {TIMESCALE_OPTIONS.map((opt) => {
              // Disabled rather than hidden: a toggle silently losing a
              // button reads as a bug, and the tooltip says why.
              //
              // ToggleButton must stay the DIRECT child of
              // ToggleButtonGroup — the group clones its direct children
              // to inject selected/onChange and the grouped borders, so
              // wrapping the button itself would break selection and
              // clicking. The tooltip therefore anchors to the label
              // inside, with pointerEvents restored because a disabled
              // MUI button sets pointer-events: none on its root.
              const selectable = isTimescaleSelectable(
                opt.value,
                selectableTimescales
              );
              return (
                <ToggleButton
                  key={opt.value}
                  value={opt.value}
                  disabled={disabled || !selectable}
                  sx={{
                    fontSize: 12,
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.25,
                  }}
                >
                  {selectable ? (
                    opt.label
                  ) : (
                    <Tooltip title={UNAVAILABLE_TIMESCALE_TOOLTIP}>
                      <span style={{ pointerEvents: 'auto' }}>{opt.label}</span>
                    </Tooltip>
                  )}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>

          {timescaleNote ? (
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              {timescaleNote}
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Chart name control — TextField, blur-commits.
// ---------------------------------------------------------------------------

interface ChartNameControlProps {
  chartName: string;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

const ChartNameControl: React.FC<ChartNameControlProps> = ({
  chartName,
  onEditField,
  disabled,
}) => {
  // Local input state so typing doesn't fire a dispatch per keystroke;
  // commit happens on blur. Mirrors the DA DatePicker onAccept pattern.
  const [draft, setDraft] = useState(chartName);

  // Re-sync local draft when the prop changes externally (e.g., agent
  // re-emission, or a different card lands on this body). Don't clobber
  // active edits — only re-sync when prop and committed draft diverge
  // AND the user isn't actively typing (controlled by isFocusedRef? — we
  // keep it simple: re-sync whenever the prop changes, since blur
  // commit means draft is rarely "owned" by the user mid-edit).
  React.useEffect(() => {
    setDraft(chartName);
  }, [chartName]);

  const handleBlur = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed === (chartName ?? '').trim()) return;
    onEditField({ chart_name: trimmed });
  }, [draft, chartName, onEditField]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: 'text.secondary',
          width: 120,
          flexShrink: 0,
          lineHeight: 1.5,
          pt: 0.75,
        }}
      >
        Chart name
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          size="small"
          variant="outlined"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder="Untitled chart"
          inputProps={{ 'aria-label': 'Chart name' }}
          sx={{
            minWidth: 240,
            maxWidth: 480,
            '& .MuiInputBase-input': { fontSize: 13 },
            '& input': { fontSize: 13 },
          }}
        />
      </Box>
    </Box>
  );
};
